import { useEffect, useMemo } from 'react';

import { debounce } from '@mui/material';
import { FormikProps } from 'formik';

import {
  adminResetValue,
  adminWorkshiftDetails,
  getWorkshiftDetails,
  userPreferredTimeFormat,
} from '@pharmaplan/common';

import {
  toShiftDetailsLabel,
  toShiftUrlParams,
} from '../../../components/Admin/BroadcastList/CreateEditBroadcastNotification/BroadcastFields/BroadcastNavigationFields/helper';
import { useAppDispatch } from '../../useAppDispatch';
import { useAppSelector } from '../../useAppSelector';

const workshiftDetailsKey = 'workshiftDetails';
const shiftDetailsKey = 'shiftDetails';
const shiftUrlDebounce = 500;

// LOADS THE SHIFT BEHIND THE PASTED URL SO ITS SUMMARY CAN BE SHOWN
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const useBroadcastShiftDetails = (formik: FormikProps<any>) => {
  const dispatch = useAppDispatch();

  const { shiftUrl } = formik.values;

  const timeFormat = useAppSelector(userPreferredTimeFormat);
  const workshiftDetails = useAppSelector(adminWorkshiftDetails);

  const shiftParams = toShiftUrlParams(shiftUrl ?? '');

  const fetchShiftDetails = useMemo(
    () =>
      debounce(({ id, isExternal }: { id: string; isExternal: boolean }) => {
        dispatch(getWorkshiftDetails({ id, isAutoSyncRecord: isExternal }));
      }, shiftUrlDebounce),
    [dispatch],
  );

  // ONLY LABEL DETAILS THAT BELONG TO THE SHIFT THE PASTED URL POINTS AT
  const shiftDetails = workshiftDetails.workshiftId === shiftParams?.id
    ? toShiftDetailsLabel(workshiftDetails, timeFormat)
    : '';

  useEffect(() => {
    if (!shiftParams) {
      dispatch(adminResetValue([workshiftDetailsKey]));
      return;
    }

    fetchShiftDetails(shiftParams);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shiftParams?.id, shiftParams?.isExternal]);

  useEffect(() => {
    formik.setFieldValue(shiftDetailsKey, shiftDetails);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shiftDetails]);

  useEffect(
    () =>
      () => {
        fetchShiftDetails.clear();
        dispatch(adminResetValue([workshiftDetailsKey]));
      },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
};

export default useBroadcastShiftDetails;
